import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { ProcessMachineMapDocument } from '../../types/processMachineMap';
import { setWorkspaceView } from '../ui/uiSlice';

export type MindmapMapMarker = {
  id: string;
  name: string;
  /** Source location from process-machine map (pre-canvas). */
  locX: number;
  locY: number;
};

export type MindmapState = {
  /** Uploaded process–machine map document (persists across map/mindmap toggles). */
  document: ProcessMachineMapDocument | null;
  fileName: string | null;
  /** Selected mindmap node id. */
  nodeId: string | null;
  nodeName: string | null;
  nodeType: string | null;
  /** Selected node + all descendant ids (for graph highlighting). */
  relatedIds: string[];
  /** Machines under the selection that have coordinates (for floor-plan overlay). */
  markers: MindmapMapMarker[];
  /** Total machines under selection (including those without location). */
  machineCount: number;
  /** Bbox of all known machine locations in the loaded dataset (for mapping). */
  locationBounds: {
    minX: number;
    minY: number;
    maxX: number;
    maxY: number;
  } | null;
};

const initialState: MindmapState = {
  document: null,
  fileName: null,
  nodeId: null,
  nodeName: null,
  nodeType: null,
  relatedIds: [],
  markers: [],
  machineCount: 0,
  locationBounds: null,
};

const mindmapSlice = createSlice({
  name: 'mindmap',
  initialState,
  reducers: {
    setMindmapDocument: (
      state,
      action: PayloadAction<{ document: ProcessMachineMapDocument; fileName: string }>
    ) => {
      state.document = action.payload.document;
      state.fileName = action.payload.fileName;
      state.nodeId = null;
      state.nodeName = null;
      state.nodeType = null;
      state.relatedIds = [];
      state.markers = [];
      state.machineCount = 0;
      state.locationBounds = null;
    },
    clearMindmapDocument: () => initialState,
    setMindmapLocationBounds: (
      state,
      action: PayloadAction<MindmapState['locationBounds']>
    ) => {
      state.locationBounds = action.payload;
    },
    setMindmapHighlight: (
      state,
      action: PayloadAction<{
        nodeId: string;
        nodeName: string;
        nodeType: string;
        relatedIds: string[];
        markers: MindmapMapMarker[];
        machineCount: number;
      }>
    ) => {
      state.nodeId = action.payload.nodeId;
      state.nodeName = action.payload.nodeName;
      state.nodeType = action.payload.nodeType;
      state.relatedIds = action.payload.relatedIds;
      state.markers = action.payload.markers;
      state.machineCount = action.payload.machineCount;
    },
    clearMindmapHighlight: (state) => {
      state.nodeId = null;
      state.nodeName = null;
      state.nodeType = null;
      state.relatedIds = [];
      state.markers = [];
      state.machineCount = 0;
    },
  },
  extraReducers: (builder) => {
    // Closing the mindmap drawer (or switching to Map) clears floor markers.
    builder.addCase(setWorkspaceView, (state, action) => {
      if (action.payload === 'map') {
        state.nodeId = null;
        state.nodeName = null;
        state.nodeType = null;
        state.relatedIds = [];
        state.markers = [];
        state.machineCount = 0;
      }
    });
  },
});

export const {
  setMindmapDocument,
  clearMindmapDocument,
  setMindmapLocationBounds,
  setMindmapHighlight,
  clearMindmapHighlight,
} = mindmapSlice.actions;

export default mindmapSlice.reducer;
